import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowRight, ArrowLeft } from "lucide-react";
import { GUIDES } from "@/data/guides";
import { ProductCard } from "@/components/ProductCard";
import { api } from "@/lib/api";

export function GuidesList() {
  useEffect(() => window.scrollTo(0, 0), []);
  return (
    <div className="pt-16" data-testid="guides-page">
      <div className="bg-charcoal border-b border-white/10">
        <div className="max-w-[1100px] mx-auto px-5 py-14">
          <p className="overline">Learn</p>
          <h1 className="font-serif text-4xl sm:text-5xl text-cream mt-2">Buying Guides & Education</h1>
          <p className="text-cream/70 mt-3 max-w-2xl">Everything you need to choose with confidence — from infrared vs. traditional to contrast therapy.</p>
        </div>
      </div>
      <div className="max-w-[1100px] mx-auto px-5 py-14 grid md:grid-cols-2 gap-6">
        {GUIDES.map((g) => (
          <Link key={g.slug} to={`/guides/${g.slug}`} data-testid={`guide-${g.slug}`}
            className="group block bg-charcoal border border-white/10 overflow-hidden hover:border-gold/30 transition-colors">
            <div className="aspect-[16/9] overflow-hidden">
              <img src={g.image} alt={g.title} className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" />
            </div>
            <div className="p-6">
              <h2 className="font-serif text-2xl text-cream group-hover:text-gold transition-colors">{g.title}</h2>
              <p className="text-cream/65 text-sm mt-2 leading-relaxed">{g.excerpt}</p>
              <span className="mt-3 inline-flex items-center gap-1 text-gold text-sm">Read guide <ArrowRight size={15} /></span>
            </div>
          </Link>
        ))}
      </div>
    </div>
  );
}

export function GuideDetail() {
  const { slug } = useParams();
  const guide = GUIDES.find((g) => g.slug === slug);
  const [recs, setRecs] = useState([]);
  useEffect(() => {
    window.scrollTo(0, 0);
    if (guide && guide.category !== "commercial") {
      api.getProducts({ category: guide.category, sort: "rating" }).then((d) => setRecs((d.products || []).slice(0, 3)));
    } else if (guide) {
      api.getProducts({ commercial: true }).then((d) => setRecs((d.products || []).slice(0, 3)));
    }
  }, [slug]); // eslint-disable-line

  if (!guide) return <div className="pt-32 text-center text-cream/60">Guide not found. <Link to="/guides" className="text-gold underline">Back to guides</Link></div>;

  return (
    <div className="pt-16" data-testid="guide-detail-page">
      <section className="relative py-28 overflow-hidden">
        <img src={guide.image} alt="" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-espresso/80" />
        <div className="relative max-w-3xl mx-auto px-5">
          <Link to="/guides" className="flex items-center gap-1 text-gold text-sm mb-4"><ArrowLeft size={15} /> All guides</Link>
          <h1 className="font-serif text-4xl sm:text-5xl text-cream leading-tight">{guide.title}</h1>
        </div>
      </section>
      <article className="max-w-3xl mx-auto px-5 py-14 space-y-10">
        {guide.sections.map((s, i) => (
          <div key={i}>
            <h2 className="font-serif text-2xl text-cream mb-3">{s.h}</h2>
            <p className="text-cream/75 leading-relaxed">{s.p}</p>
          </div>
        ))}
      </article>
      {recs.length > 0 && (
        <section className="max-w-[1100px] mx-auto px-5 pb-20">
          <div className="gold-divider mb-10" />
          <h2 className="font-serif text-3xl text-cream mb-8">Recommended Products</h2>
          <div className="grid sm:grid-cols-3 gap-5">{recs.map((p) => <ProductCard key={p.id} product={p} />)}</div>
        </section>
      )}
    </div>
  );
}
